// Menú de usuario de la barra superior (escritorio): avatar, nombre y rol; abre un menú con
// "Mi perfil" y "Cerrar sesión". En móvil/tablet la cuenta sigue en la navegación inferior.
import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { ROLE_LABELS } from '../../domain/roles.ts';
import { AccountIcon, ChevronDownIcon, LogoutIcon } from '../../ui/icons.tsx';
import { useLogout } from '../account/use-logout.tsx';
import { useProfile } from '../auth/auth-context.ts';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export function UserMenu() {
  const profile = useProfile();
  const menuId = useId();
  const logout = useLogout();
  // Igual que la campana: abierto solo en la ruta donde se abrió.
  const location = useLocation();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location.pathname;
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointer = (event: PointerEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpenAt(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenAt(null);
        button.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Flechas, Inicio y Fin recorren las opciones; Tab sale del menú y lo cierra.
  function onMenuKey(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Tab') {
      setOpenAt(null);
      return;
    }
    const items = Array.from(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'ArrowDown'
        ? items[(index + 1) % items.length]
        : event.key === 'ArrowUp'
          ? items[(index - 1 + items.length) % items.length]
          : event.key === 'Home'
            ? items[0]
            : event.key === 'End'
              ? items[items.length - 1]
              : undefined;
    if (next) {
      event.preventDefault();
      next.focus();
    }
  }

  return (
    <>
      <div className="user-menu" ref={wrapper}>
        <button
          ref={button}
          type="button"
          className={`user-menu-trigger${open ? ' user-menu-open' : ''}`}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={`Cuenta de ${profile.name}`}
          onClick={() => setOpenAt(open ? null : location.pathname)}
        >
          <span className="avatar" aria-hidden="true">
            {initials(profile.name)}
          </span>
          <span className="user-menu-text">
            <span className="user-menu-name">{profile.name}</span>
            <span className="user-menu-role">{ROLE_LABELS[profile.role]}</span>
          </span>
          <span className="user-menu-chevron">
            <ChevronDownIcon />
          </span>
        </button>
        {open ? (
          <div
            ref={menu}
            id={menuId}
            className="popover user-menu-popover"
            role="menu"
            aria-label="Cuenta"
            onKeyDown={onMenuKey}
          >
            <div className="user-menu-header">
              <span className="user-menu-name">{profile.name}</span>
              <span className="user-menu-email">{profile.email}</span>
            </div>
            <div className="user-menu-items">
              <Link to="/account" role="menuitem" className="user-menu-item" onClick={() => setOpenAt(null)}>
                <AccountIcon />
                Mi perfil
              </Link>
              <button
                type="button"
                role="menuitem"
                className="user-menu-item user-menu-item-danger"
                onClick={() => {
                  setOpenAt(null);
                  void logout.requestLogout();
                }}
              >
                <LogoutIcon />
                Cerrar sesión
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {logout.dialog}
    </>
  );
}
